<script setup>
import { Form, Field, ErrorMessage } from 'vee-validate'
import * as yup from 'yup'
import Checkbox from 'primevue/checkbox'

const store = useLivroDiarioAuxiliarStore()
const { filtros, modalFiltrosAvancados } = storeToRefs(store)
const router = useRouter()
const route = useRoute()

const contas = ref([])

// Form validation schema
const schema = yup.object({
  conta_id: yup.string(),
  vencimentoInicio: yup.date()
    .required('Data inicial é obrigatória')
    .typeError('Data inicial inválida'),
  vencimentoFim: yup.date()
    .required('Data final é obrigatória')
    .typeError('Data final inválida')
    .min(yup.ref('vencimentoInicio'), 'Data final deve ser maior que a data inicial'),
})

const initialValues = computed(() => ({
  vencimentoInicio: modalFiltrosAvancados.value.filtros.vencimentoInicio,
  vencimentoFim: modalFiltrosAvancados.value.filtros.vencimentoFim,
}))

function aplicarFiltrosAvancados (values) {
  const filtros = {
    ...modalFiltrosAvancados.value.filtros,
    vencimentoInicio: values.vencimentoInicio,
    vencimentoFim: values.vencimentoFim,
  }

  store.pesquisarComFiltrosAvancados(filtros)

  // Atualizar URL com os filtros aplicados
  const query = {
    ...route.query,
    vencimentoInicio: filtros.vencimentoInicio,
    vencimentoFim: filtros.vencimentoFim,
  }

  // Adicionar outros filtros apenas se estiverem preenchidos
  if (filtros.valor) {
    query.valor = filtros.valor
    query.valorComparacao = filtros.valorComparacao
  } else {
    delete query.valor
    delete query.valorComparacao
  }

  if (filtros.conta && typeof filtros.conta === 'object' && filtros.conta.value) {
    query.conta = filtros.conta.value
  } else {
    delete query.conta
  }

  if (filtros.possuiAnexo !== null) {
    query.possuiAnexo = filtros.possuiAnexo.toString()
  } else {
    delete query.possuiAnexo
  }

  if (filtros.statusPagamento !== null) {
    query.statusPagamento = filtros.statusPagamento.toString()
  } else {
    delete query.statusPagamento
  }

  if (modalFiltrosAvancados.value.filtros.despesas_nao_efetivas !== true) {
    query.despesas_nao_efetivas = modalFiltrosAvancados.value.filtros.despesas_nao_efetivas.toString()
  } else {
    delete query.despesas_nao_efetivas
  }

  // Navegar para a URL atualizada sem recarregar a página
  router.push({
    path: route.path,
    query,
  })

  modalFiltrosAvancados.value.exibir = false
  modalFiltrosAvancados.value.pesquisaRealizada = true
}

function limparFiltros () {
  const firstDay = new Date(filtros.value.ano, filtros.value.mes - 1, 1)
  const lastDay = new Date(filtros.value.ano, filtros.value.mes, 0)

  modalFiltrosAvancados.value.filtros = {
    vencimentoInicio: firstDay.toISOString().split('T')[0],
    vencimentoFim: lastDay.toISOString().split('T')[0],
    valor: '',
    valorComparacao: 'Igual',
    possuiAnexo: null,
    conta: {},
    tipo: 'todos',
    statusPagamento: null,
  }
}

function cancelar () {
  modalFiltrosAvancados.value.exibir = false
}

// Initialize dates when component is mounted
onMounted(async () => {
  if (!modalFiltrosAvancados.value.filtros.vencimentoInicio) {
    limparFiltros()
  }
  await usePrestacaoDeContaStore().fetchContas()
  contas.value = usePrestacaoDeContaStore().contasAgrupadasPorCategoria
})
</script>

<template>
  <CModal
    :visible="modalFiltrosAvancados.exibir"
    aria-labelledby="filtrosAvancadosLabel"
    size="lg"
    @close="cancelar"
  >
    <CModalHeader>
      <CModalTitle id="filtrosAvancadosLabel">
        Filtros Avançados
      </CModalTitle>
    </CModalHeader>

    <Form
      :validation-schema="schema"
      :initial-values="initialValues"
      @submit="aplicarFiltrosAvancados"
    >
      <CModalBody>
        <CRow>
          <CCol :md="12">
            <div class="alert alert-info">
              <p class="mb-0">
                <strong>Atenção:</strong> A pesquisa avançada pode demorar um pouco mais para ser concluída.
              </p>
            </div>
            <CFormLabel for="tipo">
              Tipo
            </CFormLabel>
            <CFormSelect
              id="tipo"
              v-model="filtros.receitaDespesa"
              color="dark"
              variant="outline"
            >
              <option value="todos">
                Todos
              </option>
              <option value="receita">
                Receitas
              </option>
              <option value="despesa">
                Despesas
              </option>
            </CFormSelect>
          </CCol>
        </CRow>
        <CRow v-if="filtros.receitaDespesa === 'despesa'" class="mt-3">
          <CCol>
            <CFormLabel for="despesa_efetiva" class="d-flex align-items-center">
              <Checkbox v-model="modalFiltrosAvancados.filtros.despesas_nao_efetivas" binary input-id="despesa_efetiva" />
              <span class="ms-2">
                Todas as despesas &nbsp;
                <CIcon v-c-tooltip="'Inclui as despesas DIRETAS e INDIRETAS'" icon="cil-info" />
              </span>
            </CFormLabel>
          </CCol>
        </CRow>
        <CRow class="mt-3">
          <CCol :md="12">
            <CFormLabel for="vencimentoInicio">
              Vencimento
            </CFormLabel>
            <div class="d-flex gap-2">
              <div class="d-flex flex-column">
                De
                <Field v-slot="{ field, meta }" name="vencimentoInicio">
                  <CFormInput
                    id="vencimentoInicio"
                    v-bind="field"
                    type="date"
                    :class="useStore().retornarClasseDeValidacao(meta)"
                  />
                  <ErrorMessage name="vencimentoInicio" class="invalid-feedback" />
                </Field>
              </div>
              <div class="d-flex flex-column">
                Até
                <Field v-slot="{ field, meta }" name="vencimentoFim">
                  <CFormInput
                    id="vencimentoFim"
                    v-bind="field"
                    type="date"
                    :class="useStore().retornarClasseDeValidacao(meta)"
                  />
                  <ErrorMessage name="vencimentoFim" class="invalid-feedback" />
                </Field>
              </div>
            </div>
          </CCol>
        </CRow>
        <CRow class="mt-3">
          <CCol :md="12">
            <CFormLabel for="conta">
              Conta
            </CFormLabel>
            <Field v-slot="{ field, meta }" v-model="modalFiltrosAvancados.filtros.conta" name="conta_id">
              <Multiselect
                v-bind="field"
                :options="contas"
                :searchable="true"
                :create-option="false"
                placeholder="Selecione ou pesquise"
                :class="useStore().retornarClasseDeValidacao(meta)"
                :groups="true"
              />
            </Field>
            <ErrorMessage name="conta_id" class="invalid-feedback" />
          </CCol>
        </CRow>
        <CRow class="mt-3">
          <CCol :md="12">
            <CFormLabel for="valor">
              Valor
            </CFormLabel>
            <div class="d-flex gap-2">
              <CFormSelect id="valorComparacao" v-model="modalFiltrosAvancados.filtros.valorComparacao">
                <option value="Igual">
                  Igual
                </option>
                <option value="Maior que">
                  Maior que
                </option>
                <option value="Menor que">
                  Menor que
                </option>
              </CFormSelect>
              <CFormInput id="valor" v-model="modalFiltrosAvancados.filtros.valor" type="number" />
            </div>
          </CCol>
        </CRow>
        <CRow class="mt-3">
          <CCol :md="12">
            <CFormLabel>Com anexo</CFormLabel>
            <div>
              <input
                id="anexoSim"
                v-model="modalFiltrosAvancados.filtros.possuiAnexo"
                class="form-check-input me-2"
                type="radio"
                :value="true"
                label="Sim"
              >
              <label>Sim</label>
              </input>
              <input
                id="anexoNao"
                v-model="modalFiltrosAvancados.filtros.possuiAnexo"
                class="form-check-input ms-4 me-2"
                type="radio"
                :value="false"
                label="Não"
              >
              <label>Não</label>
              </input>
              <input
                id="anexoTodos"
                v-model="modalFiltrosAvancados.filtros.possuiAnexo"
                class="form-check-input ms-4 me-2"
                type="radio"
                :value="null"
                label="Indiferente"
              >
              <label>Indiferente</label>
              </input >
            </div>
          </CCol>
        </CRow>
        <CRow v-if="filtros.receitaDespesa === 'despesa'" class="mt-3">
          <CCol :md="12">
            <CFormLabel>Status de Pagamento</CFormLabel>
            <div>
              <input
                id="statusPago"
                v-model="modalFiltrosAvancados.filtros.statusPagamento"
                class="form-check-input me-2"
                type="radio"
                :value="true"
                label="Pago"
              >
              <label>Pago</label>
              </input>
              <input
                id="statusNaoPago"
                v-model="modalFiltrosAvancados.filtros.statusPagamento"
                class="form-check-input ms-4 me-2"
                type="radio"
                :value="false"
                label="Não Pago"
              >
              <label>Não Pago</label>
              </input>
              <input
                id="statusIndiferente"
                v-model="modalFiltrosAvancados.filtros.statusPagamento"
                class="form-check-input ms-4 me-2"
                type="radio"
                :value="null"
                label="Indiferente"
              >
              <label>Indiferente</label>
              </input >
            </div>
          </CCol>
        </CRow>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" @click="limparFiltros">
          Limpar Filtros
        </CButton>
        <CButton color="secondary" @click="cancelar">
          Cancelar
        </CButton>
        <CButton color="primary" type="submit">
          Aplicar Filtros
        </CButton>
      </CModalFooter>
    </Form>
  </CModal>
</template>
